import { DataMode } from '../types/census';
import { AspopRow } from '../types/networks';
import { aspopSnapshot, ASPOP_SNAPSHOT_AS_OF } from '../data/networksSnapshot';
import { GLOBAL_INTERNET_USERS } from './estimates';
import { todayIso } from './format';

export interface AspopResult {
  mode: DataMode;
  asOf: string;
  rows: AspopRow[];
}

export const aspopUrl = (cca2: string) => `https://stats.labs.apnic.net/aspop/${cca2.toUpperCase()}`;
export const ripeUrl = (cca2: string) => `https://stat.ripe.net/data/country-asns/data.json?resource=${cca2.toUpperCase()}`;

const cache = new Map<string, Promise<AspopResult>>();

const num = (s: string) => Number(s.replace(/[,%\s]/g, ''));
const isNum = (s: string) => /^[\d.]+$/.test(s.replace(/[,%\s]/g, '')) && s.trim() !== '';

export function cleanOperator(raw: string): string {
  let s = raw.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  s = s.replace(/,\s*[A-Z]{2}$/, '');
  const m = s.match(/^([A-Z0-9][A-Z0-9_.-]*)\s+(.+)$/);
  if (m && m[2] && /[A-Za-z]/.test(m[2]) && (/[-_]/.test(m[1]) || m[1].length > 2)) s = m[2];
  s = s.replace(/(?:[\s-](?:AS-AP|AS-AU|AS-ID|AS-IN|AS|AP))+$/i, '');
  return s.trim() || raw.trim();
}

function fromDom(html: string): AspopRow[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const rows: AspopRow[] = [];
  doc.querySelectorAll('tr').forEach((tr) => {
    const cells = Array.from(tr.querySelectorAll('td')).map((td) => (td.textContent ?? '').trim());
    const asIdx = cells.findIndex((c) => /^AS\d+$/i.test(c));
    if (asIdx < 0) return;
    const nums = cells.slice(asIdx + 2).filter(isNum).map(num);
    if (nums.length === 0) return;
    rows.push({
      rank: isNum(cells[0] ?? '') ? num(cells[0]) : rows.length + 1,
      asn: cells[asIdx].toUpperCase(),
      name: cleanOperator(cells[asIdx + 1] ?? ''),
      users: nums[0],
      pctCountryUsers: nums[1] ?? null,
      pctInternet: nums[2] ?? null,
    });
  });
  return rows;
}

function fromScript(html: string): AspopRow[] {
  const re = /\[\s*(\d+)\s*,\s*["'](?:<[^>]*>)?\s*AS(\d+)\s*(?:<\/a>)?["']\s*,\s*["']([^"']*)["']\s*,\s*["']?([A-Z]{2})?["']?\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/g;
  const rows: AspopRow[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    rows.push({ rank: Number(m[1]), asn: `AS${m[2]}`, name: cleanOperator(m[3]), users: Number(m[5]), pctCountryUsers: Number(m[6]), pctInternet: Number(m[7]) });
  }
  return rows;
}

function snapshot(cca2: string): AspopResult {
  const rows = (aspopSnapshot[cca2.toUpperCase()] ?? []).map((r) => ({ ...r, pctCountryUsers: null, pctInternet: (r.users / GLOBAL_INTERNET_USERS) * 100 }));
  return { mode: 'snapshot', asOf: ASPOP_SNAPSHOT_AS_OF, rows };
}

export function getAspop(cca2: string): Promise<AspopResult> {
  const key = cca2.toUpperCase();
  if (!cache.has(key)) {
    cache.set(
      key,
      (async () => {
        try {
          const ctrl = new AbortController();
          const t = setTimeout(() => ctrl.abort(), 15000);
          const res = await fetch(aspopUrl(key), { signal: ctrl.signal });
          clearTimeout(t);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const html = await res.text();
          let rows = fromDom(html);
          if (rows.length === 0) rows = fromScript(html);
          rows = rows.filter((r) => r.users > 0).sort((a, b) => b.users - a.users).slice(0, 10).map((r, i) => ({ ...r, rank: i + 1 }));
          if (rows.length === 0) throw new Error('Parse failed');
          return { mode: 'live' as const, asOf: `fetched ${todayIso()}`, rows };
        } catch {
          return snapshot(key);
        }
      })(),
    );
  }
  return cache.get(key) as Promise<AspopResult>;
}
